// "Live from day one" (after Attio): one short statement on ink with a blue glow, then a
// drawing of a team's ClearMakes workspace whose activity fills in while you watch.
import { ArrowRight, Bot, CalendarDays, MessageSquareText, MoreHorizontal, Plus, Sparkles, Star, Workflow } from "lucide-react";
import { SmartLink } from "@/components/Header";
import { integrationByKey } from "@/data/site";
import { GET_STARTED_URL } from "@/data/site";
import { Rise, useStory } from "./motion";

const INBOX = "/brand/inbox-mark-256.png";
const AGENTS = "/brand/agents-mark-256.png";
const MEET = "/brand/meet-mark-256.png";

const ACTIVITY = [
  { logo: INBOX, who: "Email to Slack", what: "posted a summary to", target: "#support", when: "2 minutes ago" },
  { logo: AGENTS, who: "Support agent", what: "answered a visitor on", target: "acme.com", when: "6 minutes ago" },
  { logo: MEET, who: "ClearMeet", what: "wrote the notes for", target: "Weekly pilot review", when: "1 hour ago" },
  { logo: INBOX, who: "Lead to HubSpot", what: "added", target: "Contoso", when: "2 hours ago" },
];

const Highlight = ({ label, icon: Icon, children, lit }: { label: string; icon: typeof Star; children: React.ReactNode; lit: boolean }) => (
  <div className={`rounded-xl border bg-background p-4 transition-all duration-500 ${lit ? "translate-y-0 border-border opacity-100" : "translate-y-2 border-transparent opacity-0"}`}>
    <p className="flex items-center justify-between text-[13px] text-muted-foreground">
      {label} <Icon className="h-4 w-4" />
    </p>
    <div className="mt-3">{children}</div>
  </div>
);

const Workspace = () => {
  const { ref, step } = useStory(ACTIVITY.length + 3, 650, 4000);
  return (
    <div ref={ref} aria-hidden className="overflow-hidden rounded-2xl border border-[hsl(0_0%_86%)] bg-background text-left shadow-[0_40px_80px_-40px_hsl(216_60%_20%/0.35)]">
      <div className="grid lg:grid-cols-[300px_1fr]">
        {/* the team, like a record */}
        <div className="border-b border-border p-6 lg:border-b-0 lg:border-r">
          <div className="flex items-start justify-between">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-foreground text-[16px] font-bold text-background">A</span>
            <Star className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="mt-4 text-[20px] font-semibold tracking-[-0.02em] text-foreground">Acme team</p>
          <div className="mt-3 flex gap-2">
            <span className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[13px] font-medium text-foreground"><Plus className="h-3.5 w-3.5" /> New workflow</span>
            <span className="flex items-center rounded-lg border border-border px-2 text-muted-foreground"><MoreHorizontal className="h-4 w-4" /></span>
          </div>
          <dl className="mt-6 space-y-3 text-[13.5px]">
            {[
              ["Plan", "Free"],
              ["Login", "One for every product"],
              ["Members", "4 people"],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-3">
                <dt className="w-20 shrink-0 text-muted-foreground">{k}</dt>
                <dd className="text-foreground">{v}</dd>
              </div>
            ))}
            <div className="flex gap-3">
              <dt className="w-20 shrink-0 text-muted-foreground">Apps</dt>
              <dd className="flex flex-wrap gap-1.5">
                {["gmail", "slack", "outlook", "hubspot"].map((k) => {
                  const a = integrationByKey(k)!;
                  return (
                    <span key={k} className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[12px] text-foreground">
                      <img src={a.logo} alt="" className="h-3 w-3 object-contain" /> {a.name}
                    </span>
                  );
                })}
              </dd>
            </div>
          </dl>
        </div>

        {/* what is happening */}
        <div className="bg-[hsl(40_9%_98%)] p-5 sm:p-6">
          <p className="flex items-center gap-2 text-[15px] font-semibold text-foreground"><Sparkles className="h-4 w-4" /> Highlights</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Highlight label="Inbox" icon={Workflow} lit={step >= 0}>
              <p className="text-[16px] font-semibold text-foreground">3 workflows on</p>
              <p className="text-[13px] text-muted-foreground">Email to Slack, Lead to HubSpot, FAQ reply</p>
            </Highlight>
            <Highlight label="Agents" icon={Bot} lit={step >= 1}>
              <p className="text-[16px] font-semibold text-foreground">Support agent</p>
              <p className="text-[13px] text-muted-foreground">On acme.com and for the team</p>
            </Highlight>
            <Highlight label="ClearMeet" icon={CalendarDays} lit={step >= 2}>
              <p className="text-[16px] font-semibold text-foreground">Weekly pilot review</p>
              <p className="text-[13px] text-muted-foreground">Thursday, 10:00</p>
            </Highlight>
          </div>

          <p className="mt-6 flex items-center gap-2 text-[15px] font-semibold text-foreground"><MessageSquareText className="h-4 w-4" /> Activity</p>
          <ul className="mt-3 overflow-hidden rounded-xl border border-border bg-background">
            {ACTIVITY.map((a, i) => (
              <li
                key={a.who + a.target}
                className={`flex items-center gap-3 border-b border-border px-4 py-3 text-[14px] transition-all duration-500 last:border-0 ${
                  step >= i + 3 ? "translate-x-0 opacity-100" : "translate-x-3 opacity-0"
                }`}
              >
                <img src={a.logo} alt="" className="h-5 w-5 shrink-0 object-contain" />
                <span className="min-w-0 flex-1 truncate text-foreground">
                  <span className="font-medium">{a.who}</span> <span className="text-muted-foreground">{a.what}</span>{" "}
                  <span className="underline decoration-border underline-offset-4">{a.target}</span>
                </span>
                <span className="hidden shrink-0 text-[12.5px] text-muted-foreground sm:block">{a.when}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export const DayOne = () => (
  <section aria-labelledby="dayone-h" className="relative overflow-hidden bg-[hsl(0_0%_4%)] text-[hsl(0_0%_100%)]">
    <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(55%_45%_at_50%_0%,hsl(216_84%_52%/0.28),transparent_70%),radial-gradient(70%_50%_at_50%_100%,hsl(216_84%_45%/0.45),transparent_70%)]" />
    <div className="relative mx-auto max-w-[1180px] px-4 pb-24 pt-24 sm:px-8 sm:pb-32 sm:pt-32">
      <Rise className="text-center">
        <h2 id="dayone-h" className="mx-auto max-w-[900px] text-[2rem] font-semibold leading-[1.12] tracking-[-0.035em] text-[hsl(0_0%_100%)] sm:text-[3rem]">
          Live from day one.{" "}
          <span className="text-[hsl(216_25%_65%)]">One login for every product. Your first workflow runs the same day.</span>
        </h2>
        <SmartLink href={GET_STARTED_URL} className="group mt-9 inline-flex h-11 items-center gap-2 rounded-xl bg-[hsl(0_0%_100%)] px-5 text-[15px] font-semibold text-[hsl(0_0%_4%)] transition-colors hover:bg-[hsl(216_84%_52%)] hover:text-[hsl(0_0%_100%)]">
          Get started free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </SmartLink>
      </Rise>
      <Rise delay={0.1} y={40} className="mt-16">
        <Workspace />
      </Rise>
    </div>
  </section>
);
